export function normalizeClientIdentity(value: unknown): string {
  return String(value ?? '').trim();
}

export function shouldApplyClientDetailResponse(input: {
  requestedId: string;
  selectedId: string | null | undefined;
  responseClientId?: unknown;
}): boolean {
  const requested = normalizeClientIdentity(input.requestedId);
  const selected = normalizeClientIdentity(input.selectedId);
  const returned = normalizeClientIdentity(input.responseClientId);
  return Boolean(requested && selected && returned && requested === selected && returned === selected);
}

export async function fetchClientDetailIfSelected<T extends { client?: { id?: unknown } }>(input: {
  requestedId: string;
  getSelectedId: () => string | null | undefined;
  apply: (data: T) => void;
  fetchImpl?: typeof fetch;
}): Promise<'applied' | 'ignored' | 'failed'> {
  const fetchImpl = input.fetchImpl || fetch;
  const res = await fetchImpl(`/api/clients/${input.requestedId}`);
  if (!res.ok) return 'failed';
  const data = await res.json() as T;
  if (!shouldApplyClientDetailResponse({
    requestedId: input.requestedId,
    selectedId: input.getSelectedId(),
    responseClientId: data?.client?.id
  })) {
    return 'ignored';
  }
  input.apply(data);
  return 'applied';
}

/**
 * GET /api/clients/:id returns the chart wrapper `{ client, notes, documents, ... }`.
 * Callers that need the client row must read `client`, not the wrapper.
 * A response for a different client is ignored.
 */
export function clientRecordFromChartResponse(response: unknown, fallback?: { id?: unknown } | null): any {
  if (!response || typeof response !== 'object') return fallback ?? null;
  const body = response as {
    client?: any;
    id?: unknown;
    first_name?: unknown;
    last_name?: unknown;
    physician_phone?: unknown;
  };
  const wrapped = body.client;
  if (wrapped && typeof wrapped === 'object' && !Array.isArray(wrapped)) {
    const matches = !fallback?.id || shouldApplyClientDetailResponse({
      requestedId: String(fallback.id),
      selectedId: String(fallback.id),
      responseClientId: wrapped.id
    });
    return matches && wrapped.id ? wrapped : (fallback ?? null);
  }
  const looksLikeClient = body.id != null && (
    body.first_name !== undefined || body.last_name !== undefined || body.physician_phone !== undefined
  );
  if (looksLikeClient && (!fallback?.id || String(body.id) === String(fallback.id))) return body;
  return fallback ?? null;
}

/** A chart wrapper or a client row, reduced to the client row. */
export function entryClientRecord(value: unknown): any {
  if (!value || typeof value !== 'object') return null;
  const record = value as { id?: unknown };
  return clientRecordFromChartResponse(value, record.id ? record : null);
}

/** Blank for missing sentinels. Spaces stay so a controlled input can hold an address while it is typed. */
export function presentCallText(value: unknown): string {
  const text = String(value ?? '');
  const trimmed = text.trim();
  if (!trimmed || trimmed === 'undefined' || trimmed === 'null' || trimmed === 'Unavailable') return '';
  return text;
}

export function clientDisplayName(client: unknown): string {
  const record = entryClientRecord(client);
  const first = presentCallText(record?.first_name).trim();
  const last = presentCallText(record?.last_name).trim();
  return [first, last].filter(Boolean).join(' ');
}
